import React from 'react'
import { Box, Stack, Typography } from '@mui/material'

import { FieldLabel } from '@repo/ui'
import { useRolesMatrix, type MatrixRoleItem, type PermissionGroup } from 'hooks/api'

interface RolePermissionsInfoProps {
  /** Role name (e.g. 'company_admin') whose permissions to display. */
  roleName?: string
}

/**
 * Read-only preview of what the selected role can do (VERA-818 / VERA-821),
 * sourced from the same real permissions data the Roles & Permissions UI uses
 * (`useRolesMatrix` — the RolePage `PermissionsList` / `PermissionsMatrix` data
 * source), not the old static VERA-780 spec-sheet matrix. Renders one row per
 * permission group; a group where the role holds every permission collapses to
 * "Full access". Scrolls within a fixed max height rather than growing the modal.
 *
 * Every group — full-access or partial — renders its granted permissions as an
 * indented list, not just partial ones. This is an access-control decision made
 * at invite time, and collapsing a full-access group to a single "Full access"
 * word hid the contents of the MOST permissive grant: "Certificates — Full
 * access" never revealed that it includes "Delete certificate" and "Transfer
 * certificate" — exactly the permissions you'd want to see before inviting
 * someone. The right-edge coverage tag ("Full access" / "2 of 8") stays so a
 * reader can still skim overall coverage without reading every row.
 */
export const RolePermissionsInfo = ({ roleName }: RolePermissionsInfoProps) => {
  const { data } = useRolesMatrix()

  if (!roleName) return null

  const role: MatrixRoleItem | undefined = data?.roles?.find(r => r.name === roleName)
  const groups: PermissionGroup[] = data?.groups ?? []

  // Groups the role holds at least one permission in — groups it holds none of
  // are omitted (keeps the preview to "what this role CAN do", not a full grid).
  const rows = groups
    .map(group => {
      const granted = group.permissions.filter(p => role?.permissionNames.includes(p.name))
      return { group, granted, fullAccess: granted.length > 0 && granted.length === group.permissions.length }
    })
    .filter(row => row.granted.length > 0)

  return (
    <Box sx={{ mt: 1 }}>
      <FieldLabel label="Permissions" />

      {!role || rows.length === 0 ? (
        <Typography variant="body4" color="text.secondary" sx={{ display: 'block' }}>
          No permissions are associated with this role.
        </Typography>
      ) : (
        <Box
          sx={{
            maxHeight: 320,
            overflowY: 'auto',
            p: 3,
            borderRadius: 2,
            border: theme => `1px solid ${theme.palette.divider}`,
            bgcolor: 'action.hover',
          }}
        >
          <Stack spacing={2}>
            {rows.map(({ group, granted, fullAccess }) => (
              // Every group — full or partial — gets the same shape: a heading
              // row (label left, coverage tag right) then the indented list of
              // its granted permission labels.
              <Stack key={group.key} spacing={1}>
                <Stack direction="row" spacing={3} alignItems="baseline" justifyContent="space-between">
                  <Typography variant="body4" sx={{ fontWeight: 500, flexShrink: 0 }}>
                    {group.label}
                  </Typography>
                  <Typography
                    variant="body4"
                    color={fullAccess ? 'success.main' : 'text.secondary'}
                    sx={{ textAlign: 'right', fontWeight: 600, flexShrink: 0 }}
                  >
                    {fullAccess ? 'Full access' : `${granted.length} of ${group.permissions.length}`}
                  </Typography>
                </Stack>
                <Stack spacing="2px" sx={{ pl: 3 }}>
                  {granted.map(p => (
                    <Typography key={p.name} variant="body4" color="text.secondary">
                      {p.label}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  )
}
